import { useCallback, useState } from 'react'
import { useAppDispatch, useAppSelector } from '../hooks'
import { selectAccountId } from '../auth/authSlice'
import { useCompleteOnboardingFlowMutation, useDismissOnboardingFlowMutation, useDismissOnboardingStepMutation, onboardingApi } from './onboarding'
import { type OnboardingFlow, OnboardingFlowType, OnboardingStepStatus } from '../../models/Onboarding'
import { closeDismissOnboardingConfirm, openDismissOnboardingConfirm, openOnboardingCompleted } from './onboardingUiSlice'

export interface UseOnboardingActionsResult {
  completedOrSkippedCount: number
  totalCount: number
  areAllStepsCompleted: boolean
  isDismissingFlow: boolean
  isCompletingFlow: boolean
  skippingStepIds: Set<string>
  handleDismiss: () => void
  handleConfirmDismiss: () => Promise<void>
  handleComplete: () => Promise<void>
  handleSkipStep: (stepId: string) => Promise<void>
}

export const useOnboardingActions = (flow: OnboardingFlow | undefined): UseOnboardingActionsResult => {
  const dispatch = useAppDispatch()
  const accountId = useAppSelector(selectAccountId)
  const [dismissFlow, { isLoading: isDismissingFlow }] = useDismissOnboardingFlowMutation()
  const [completeFlow, { isLoading: isCompletingFlow }] = useCompleteOnboardingFlowMutation()
  const [skipStep] = useDismissOnboardingStepMutation()
  const [skippingStepIds, setSkippingStepIds] = useState<Set<string>>(new Set())

  const activeSteps = flow ? flow.steps.filter((s) => s.status !== OnboardingStepStatus.NOT_ALLOWED) : []
  const completedOrSkippedCount = flow
    ? activeSteps.filter((s) => s.status === OnboardingStepStatus.COMPLETED || s.status === OnboardingStepStatus.SKIPPED).length + 1
    : 0
  const totalCount = flow ? activeSteps.length + 1 : 0
  const areAllStepsCompleted = completedOrSkippedCount === totalCount

  const handleDismiss = useCallback(() => {
    dispatch(openDismissOnboardingConfirm())
  }, [dispatch])

  const handleConfirmDismiss = useCallback(async () => {
    if (!accountId) {
      return
    }
    await dismissFlow({ accountId, flowType: OnboardingFlowType.DEFAULT }).unwrap()
    dispatch(closeDismissOnboardingConfirm())
    dispatch(openOnboardingCompleted())
  }, [accountId, dismissFlow, dispatch])

  const handleComplete = useCallback(async () => {
    if (!accountId) {
      return
    }
    await completeFlow({ accountId, flowType: OnboardingFlowType.DEFAULT }).unwrap()
    dispatch(openOnboardingCompleted())
  }, [accountId, completeFlow, dispatch])

  const handleSkipStep = useCallback(
    async (stepId: string) => {
      if (!accountId) {
        return
      }
      setSkippingStepIds((prev) => new Set(prev).add(stepId))
      dispatch(
        onboardingApi.util.updateQueryData('getOnboardingFlow', { accountId, flowType: OnboardingFlowType.DEFAULT }, (draft) => {
          const draftStep = draft.data.steps.find((s) => s.id === stepId)
          if (draftStep) {
            draftStep.status = OnboardingStepStatus.SKIPPED
          }
        }),
      )
      try {
        await skipStep({ accountId, flowType: OnboardingFlowType.DEFAULT, stepId, status: OnboardingStepStatus.SKIPPED })
      } finally {
        setSkippingStepIds((prev) => {
          const next = new Set(prev)
          next.delete(stepId)
          return next
        })
      }
    },
    [accountId, dispatch, skipStep],
  )

  return {
    completedOrSkippedCount,
    totalCount,
    areAllStepsCompleted,
    isDismissingFlow,
    isCompletingFlow,
    skippingStepIds,
    handleDismiss,
    handleConfirmDismiss,
    handleComplete,
    handleSkipStep,
  }
}
